"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import { X, Flame } from 'lucide-react';

interface Props {
  whatsapp?: string | null;
  text?: string | null;
  buttonText?: string | null;
}

const AnnouncementBar = ({
  whatsapp,
  text,
  buttonText,
}: Props) => {
  const [closed, setClosed] = useState(false);

  if (closed) return null;

  const bannerText = text || 'Get Instant Promo Code Alerts: Join WhatsApp Channel for Daily Verified Deals';
  const bannerLink = whatsapp || '#';
  const bannerButton = buttonText || 'Join Now';
  const isExternal = bannerLink.startsWith('http') || bannerLink.startsWith('//');

  return (
    <div id="t20-announcement-bar" className="w-full bg-[#F97316] text-white py-1 sm:py-1.5 px-3 sm:px-6 flex items-center justify-between gap-2 sm:gap-3 text-[11px] md:text-sm relative z-40 leading-tight">
      <style>{`
        @keyframes t20FinalShimmer {
          0%, 100% {
            background-color: #1E3A5F;
            color: #FFFFFF;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
          }
          50% {
            background-color: #FFFFFF;
            color: #1E3A5F;
            box-shadow: 0 0 10px rgba(255, 255, 255, 0.7), 0 2px 6px rgba(0, 0, 0, 0.15);
          }
        }
        .t20-text-blink {
          animation: t20FinalShimmer 1.5s ease-in-out infinite;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          gap: 4px !important;
          cursor: pointer !important;
          text-decoration: none !important;
          transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease !important;
        }
        .t20-text-blink span {
          color: inherit !important;
        }
        .t20-text-blink:hover {
          animation-play-state: paused !important;
          background-color: #1E3A5F !important;
          color: #FFFFFF !important;
          transform: scale(1.05) !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3) !important;
        }
        .t20-text-blink:hover span {
          color: #FFFFFF !important;
        }
      `}</style>
      <div className="flex-1 flex items-center justify-center gap-2 sm:gap-2.5 flex-wrap text-center">
        <span className="font-semibold flex items-center gap-1.5">
          <Flame size={16} className="shrink-0 text-yellow-300" aria-hidden="true" />
          {bannerText}
        </span>
        {bannerLink && (
          <Link
            href={bannerLink}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            className="t20-text-blink group font-black text-[10px] sm:text-[11px] uppercase tracking-wider px-2.5 py-0.5 rounded-full no-underline cursor-pointer ml-1 leading-normal"
          >
            <span>{bannerButton}</span>
            <span aria-hidden="true" className="inline-block text-[10px] leading-none transition-transform duration-200 group-hover:translate-x-0.5">→</span>
          </Link>
        )}
      </div>
      <button
        onClick={() => setClosed(true)}
        aria-label="Close Announcement"
        className="shrink-0 text-white/80 hover:text-white transition-colors p-0.5 flex items-center justify-center cursor-pointer"
      >
        <X size={14} />
      </button>
    </div>
  );
};

export default AnnouncementBar;
